import { useState, useEffect, useRef } from "react";
import { COUNTRIES } from "@/lib/countries";

interface DetectedLocation {
  countryCode: string;
  stateCode: string;
  isLoading: boolean;
  error: string | null;
}

// Default target markets if none specified
const DEFAULT_SUPPORTED_COUNTRIES = ["AU", "NZ", "GB", "IE", "CA", "US"];

// Module-level cache to share detection result across hook instances
// This prevents multiple API calls when multiple components use the hook
let cachedDetection: { countryCode: string; stateName: string } | null = null;
let detectionPromise: Promise<{ countryCode: string; stateName: string } | null> | null = null;

/**
 * Finds the state code from our COUNTRIES data based on region name
 */
function findStateCode(countryCode: string, regionName: string): string {
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country?.states || !regionName) return "";
  
  const normalizedRegion = regionName.toLowerCase().trim();
  
  const state = country.states.find(s => 
    s.name.toLowerCase() === normalizedRegion ||
    s.code.toLowerCase() === normalizedRegion
  );
  
  return state?.code || "";
}

/**
 * Checks if a country code is supported based on the provided configuration
 * Handles both country codes (AU) and country names (Australia)
 */
function isSupportedByConfig(code: string, supportedCountries: string[]): boolean {
  // Strip GLOBAL sentinel — it's an API-only marker, not a country code
  const codes = supportedCountries.filter(c => c !== "GLOBAL");
  
  // If no specific codes remain (or empty), support all countries
  if (codes.length === 0) {
    return true;
  }
  
  // Check if code matches directly
  if (codes.includes(code)) {
    return true;
  }
  
  // Check if the country name matches
  const country = COUNTRIES.find(c => c.code === code);
  if (country && codes.includes(country.name)) {
    return true;
  }
  
  return false;
}

/**
 * Hook to detect user's location via IP geolocation
 * Configurable per generator - supports country codes, names, and "GLOBAL"
 * Returns empty values if location cannot be determined or is outside supported countries
 * 
 * @param supportedCountries - Optional array of supported country codes/names, or ["GLOBAL"] for all
 */
export function useLocationDetection(supportedCountries?: string[]): DetectedLocation {
  const [location, setLocation] = useState<DetectedLocation>({
    countryCode: "",
    stateCode: "",
    isLoading: true,
    error: null,
  });

  // Compute effective supported countries inline - no need for useMemo since we 
  // just need a stable comparison and the effect handles caching
  const effectiveSupported = supportedCountries?.length 
    ? supportedCountries 
    : DEFAULT_SUPPORTED_COUNTRIES;
  
  // Create stable dependency for effect - stringify to avoid array reference issues
  const supportedKey = effectiveSupported.join(',');

  useEffect(() => {
    let isMounted = true;
    
    // Parse the key back to array inside the effect to avoid stale closure
    const supported = supportedKey.split(',');

    async function detectLocation() {
      try {
        // Use cached result if available
        if (cachedDetection) {
          const { countryCode: detectedCountry, stateName } = cachedDetection;
          
          if (isSupportedByConfig(detectedCountry, supported)) {
            const stateCode = findStateCode(detectedCountry, stateName);
            setLocation({
              countryCode: detectedCountry,
              stateCode,
              isLoading: false,
              error: null,
            });
          } else {
            setLocation({
              countryCode: "",
              stateCode: "",
              isLoading: false,
              error: null,
            });
          }
          return;
        }

        // If a detection is already in progress, wait for it
        if (!detectionPromise) {
          detectionPromise = (async () => {
            try {
              const response = await fetch("https://api.ipapi.is/");

              if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
              }

              const data = await response.json();

              if (data.error) {
                throw new Error(data.message || "Geolocation lookup failed");
              }

              const result = {
                // ipapi.is returns country_code in data.location, not at root level
                countryCode: data.location?.country_code?.toUpperCase() || data.country_code?.toUpperCase() || "",
                stateName: data.location?.state || data.location?.region || "",
              };
              
              cachedDetection = result;
              return result;
            } catch (err) {
              // Cache a failed result so we don't keep retrying
              cachedDetection = { countryCode: "", stateName: "" };
              throw err;
            }
          })();
        }

        const result = await detectionPromise;
        
        if (!isMounted) return;

        if (result && isSupportedByConfig(result.countryCode, supported)) {
          const stateCode = findStateCode(result.countryCode, result.stateName);
          setLocation({
            countryCode: result.countryCode,
            stateCode,
            isLoading: false,
            error: null,
          });
        } else {
          setLocation({
            countryCode: "",
            stateCode: "",
            isLoading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!isMounted) return;
        
        // On any error, silently fall back to no location selected
        setLocation({
          countryCode: "",
          stateCode: "",
          isLoading: false,
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }
    }

    detectLocation();

    return () => {
      isMounted = false;
    };
  }, [supportedKey]);

  return location;
}
